import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

let connectedUserId = null

// Keeps web and native notification badges on the persisted server count. The
// channel transmits immediately on connect, then broadcasts after every create or
// read, so Turbo cache restoration and delayed foreground pushes cannot drift it.
export default class extends Controller {
  static targets = ["count"]
  static values = { userId: Number }

  connect() {
    // Turbo keeps the shared Action Cable consumer alive between page visits.
    // Impersonation changes the effective user cookie, so reconnect the socket
    // whenever the page's effective user differs from the previous one.
    if (connectedUserId !== null && connectedUserId !== this.userIdValue) {
      consumer.disconnect()
      consumer.connect()
    }
    connectedUserId = this.userIdValue

    this._onTabVisibility = event => {
      this._nativeVisible = event.detail?.visible !== false
      if (!this._nativeVisible) this.dismissBanner()
    }
    document.addEventListener("native:tab-visibility", this._onTabVisibility)
    this._nativeVisible = window.webkit?.messageHandlers?.nativeBadge
      ? window.proximityTabVisible === true : true
    this._onPageVisibility = () => {
      if (document.visibilityState !== "visible") this.dismissBanner()
    }
    document.addEventListener("visibilitychange", this._onPageVisibility)
    this._subscription = consumer.subscriptions.create(
      { channel: "NotificationChannel" },
      {
        received: (data) => {
          this.update(data?.unread_count)
          if (data?.notification) this.showBanner(data.notification)
          document.dispatchEvent(new CustomEvent("notifications:changed", { detail: data }))
        }
      }
    )
  }

  disconnect() {
    this._subscription?.unsubscribe()
    document.removeEventListener("native:tab-visibility", this._onTabVisibility)
    document.removeEventListener("visibilitychange", this._onPageVisibility)
    this.dismissBanner()
  }

  showBanner(notification) {
    if (window.proximityManualMatching && notification.kind === "library_no_matches") return
    if (notification.kind === "library_no_matches" && notification.run_key) {
      try {
        const handled = JSON.parse(sessionStorage.getItem(`manual-matching-banner:${this.userIdValue}`) || "null")
        if (handled?.runKey === notification.run_key && Date.now() - handled.at < 30_000) return
      } catch (_) { /* Keep normal banners when storage is unavailable. */ }
    }
    if (document.visibilityState !== "visible" || !this._nativeVisible) return
    if (!/^\/notifications\/\d+\/open$/.test(notification.path)) return
    this.dismissBanner()
    const banner = document.createElement("div")
    banner.className = "app-notification-banner"
    banner.setAttribute("role", "status")
    banner.setAttribute("aria-live", "polite")
    const link = document.createElement("a")
    link.href = notification.path
    link.textContent = notification.body
    const close = document.createElement("button")
    close.type = "button"
    close.textContent = "×"
    close.setAttribute("aria-label", "Dismiss notification")
    close.addEventListener("click", () => this.dismissBanner())
    banner.append(link, close)
    let startY
    banner.addEventListener("touchstart", event => { startY = event.touches[0]?.clientY }, { passive: true })
    banner.addEventListener("touchend", event => {
      if (startY - event.changedTouches[0]?.clientY > 25) this.dismissBanner()
    }, { passive: true })
    this.element.append(banner)
    this._banner = banner
    this._bannerTimer = setTimeout(() => this.dismissBanner(), 6000)
  }

  dismissBanner() {
    clearTimeout(this._bannerTimer)
    this._banner?.remove()
    this._banner = null
  }

  update(value) {
    const count = Math.max(0, parseInt(value, 10) || 0)
    const meta = document.querySelector('meta[name="unread-notifications"]')
    if (meta) meta.content = String(count)

    this.countTargets.forEach((badge) => {
      badge.textContent = count > 99 ? "99+" : String(count)
      badge.style.display = count > 0 ? "block" : "none"
    })

    if (window.webkit?.messageHandlers?.nativeBadge) {
      window.webkit.messageHandlers.nativeBadge.postMessage(count)
    }
  }
}
